/**
 * Corregir una celda de la tabla de conciliación sin salir de ella: el proveedor, el inmueble,
 * el soporte documental y las observaciones.
 *
 * Cada una lleva su lápiz al lado. Pulsarlo abre una tarjetita pegada a la celda con lo justo
 * para cambiar ESE dato; la fila sigue abriendo su panel completo al pulsarla, como antes.
 *
 * DÓNDE SE GUARDA depende de qué es la fila, y eso lo resuelve este fichero para que la tabla
 * no tenga que saberlo:
 *  · Un GASTO apuntado (con o sin cargo del banco) → el apunte.
 *  · Un MOVIMIENTO del banco sin apunte → el propio movimiento. Elegir el inmueble de un cargo
 *    pendiente es imputarlo: se crea su gasto, igual que desde «Resolver».
 *  · Un COBRO fuera del banco → el cobro (su justificante y sus observaciones; de quién es y de
 *    qué inmueble lo dice el contrato).
 *  · Lo que se esperaba y no ha llegado no es nada todavía: no se edita.
 */
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../api/client';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Input, Select, Textarea } from '../../components/form/Field';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { toast } from '../../components/feedback/Toaster';
import { avisarReapertura } from './MissingPanel';
import { aPropertyId, type FilaConciliacion } from './reconciliation';
import { GASTOS_GENERALES_ID, GASTOS_GENERALES_NOMBRE } from '@naar/shared';
import styles from './RowEditor.module.css';

export type CampoEditable = 'proveedor' | 'inmueble' | 'pagador' | 'soporte' | 'observaciones';

const TITULO: Record<CampoEditable, string> = {
  proveedor: 'Proveedor',
  inmueble: 'Inmueble',
  pagador: 'Quién paga',
  soporte: 'Soporte documental',
  observaciones: 'Observaciones',
};

/** Si esa celda de esa fila se puede corregir. */
export function puedeEditar(fila: FilaConciliacion, campo: CampoEditable): boolean {
  if (fila.prevista) return false;
  // Quién paga: solo un ingreso del banco. Un cobro fuera del banco ya se registró contra su inquilino.
  if (campo === 'pagador') return fila.movement?.kind === 'ingreso';
  if (fila.entry || fila.movement) return true;
  if (fila.payment) return campo === 'soporte' || campo === 'observaciones';
  return false;
}

/** Un cargo del banco que nadie ha explicado: su papel es un ticket, y el ticket crea el gasto. */
export function esCargoPendiente(fila: FilaConciliacion): boolean {
  return !fila.entry && fila.movement?.kind === 'gasto' && fila.movement.status === 'pendiente';
}

/**
 * Sube el papel de una fila a donde corresponde: la factura del apunte (o la cambia), el ticket
 * de un cargo pendiente (que crea su gasto en `inmueble`), el justificante del movimiento o el
 * del cobro fuera del banco.
 */
export async function subirDocumentoDeFila(
  fila: FilaConciliacion,
  file: File,
  inmueble: string,
): Promise<{ reopened?: boolean }> {
  const entry = fila.entry?.entry;
  const movement = fila.movement;
  if (entry) {
    return entry.document ? api.replaceEntryDocument(entry.id, file) : api.attachEntryDocument(entry.id, file);
  }
  if (movement && esCargoPendiente(fila)) {
    // Sin inmueble elegido, la API lo saca del nombre del fichero o de la factura (su CUPS, su dirección).
    await api.attachMovementTicket(movement.id, file, inmueble ? { propertyId: inmueble } : {});
    return {};
  }
  if (movement) return api.attachMovementDocument(movement.id, file);
  if (fila.payment) return api.attachPaymentDocument(fila.payment.id, file);
  return {};
}

/** El lápiz de una celda. No abre la fila: solo su editor. */
export function LapizCelda({
  campo,
  activo,
  onOpen,
}: {
  campo: CampoEditable;
  activo: boolean;
  onOpen: (anchor: HTMLElement) => void;
}) {
  return (
    <button
      type="button"
      className={activo ? `${styles.lapiz} ${styles.lapizActivo}` : styles.lapiz}
      title={`Editar ${TITULO[campo].toLowerCase()}`}
      aria-label={`Editar ${TITULO[campo].toLowerCase()}`}
      aria-expanded={activo}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(event.currentTarget);
      }}
      onKeyDown={(event) => {
        // Enter en el lápiz no debe llegar a la fila, que abriría su panel.
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
    >
      <Icon name="edit" size={13} strokeWidth={2} />
    </button>
  );
}

interface EditorCeldaProps {
  fila: FilaConciliacion;
  campo: CampoEditable;
  anchor: HTMLElement;
  /** El mes de la fila, para avisar si añadir un papel ha reabierto un mes conciliado. */
  month: string;
  properties: { id: string; name: string }[];
  /** Los inquilinos a los que se puede imputar un ingreso («Quién paga»). */
  tenants?: { id: string; fullName: string; label: string; propertyName: string | null; unitCode: string | null }[];
  onClose: () => void;
}

const ANCHO = 340;

/** «y 3 movimientos iguales de otros meses», para el aviso al guardar quién paga. */
function yIguales(n: number): string {
  return n > 0 ? ` y en ${n === 1 ? '1 movimiento igual' : `${n} movimientos iguales`}` : '';
}

export function EditorCelda({ fila, campo, anchor, month, properties, tenants = [], onClose }: EditorCeldaProps) {
  const queryClient = useQueryClient();
  const cajaRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const entry = fila.entry?.entry;
  const movement = fila.movement;
  const cargoPendiente = esCargoPendiente(fila);

  const [texto, setTexto] = useState(() => {
    if (campo === 'proveedor') return entry ? (entry.vendorName ?? '') : (movement?.vendorName ?? fila.proveedor ?? '');
    if (campo === 'observaciones') {
      if (entry) return entry.notes ?? '';
      if (movement) return movement.notes ?? '';
      return fila.payment?.reference ?? '';
    }
    return '';
  });
  const [inmueble, setInmueble] = useState(fila.inmueble?.id ?? '');
  // Lo guardado o, si no hay, lo que propone la IA: viene elegido pero no se guarda hasta «Guardar».
  const [pagador, setPagador] = useState(fila.pagador?.id ?? '');
  const ficheroRef = useRef<HTMLInputElement>(null);

  // --- Colocación: debajo de la celda, o encima si no cabe. --------------------
  useLayoutEffect(() => {
    const caja = cajaRef.current;
    if (!caja) return;
    const r = anchor.getBoundingClientRect();
    const alto = caja.offsetHeight;
    const left = Math.min(Math.max(12, r.right - ANCHO + 8), window.innerWidth - ANCHO - 12);
    const debajo = r.bottom + 6;
    const top = debajo + alto > window.innerHeight - 12 ? Math.max(12, r.top - alto - 6) : debajo;
    setPos({ top, left });
  }, [anchor]);

  // --- Cerrar: Esc, clic fuera, o si la tabla se desplaza debajo. --------------
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    }
    function onDown(event: MouseEvent) {
      const target = event.target as Node;
      if (cajaRef.current?.contains(target) || anchor.contains(target)) return;
      onClose();
    }
    function onScroll(event: Event) {
      if (cajaRef.current?.contains(event.target as Node)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [anchor, onClose]);

  // El primer campo, con el foco: quien pulsa el lápiz quiere escribir.
  useEffect(() => {
    const timer = setTimeout(() => {
      cajaRef.current?.querySelector<HTMLElement>('input:not([type=file]), textarea, select, button')?.focus();
    }, 30);
    return () => clearTimeout(timer);
  }, []);

  function fallo(error: unknown) {
    toast.error(error instanceof ApiError ? error.message : 'No se ha podido guardar.');
  }

  async function listo(mensaje: string, reopened?: boolean) {
    await queryClient.invalidateQueries({ queryKey: ['ledger'] });
    toast.success(mensaje);
    if (reopened) avisarReapertura(month);
    onClose();
  }

  // --- Guardar un dato (texto o inmueble) ---------------------------------------
  const guardar = useMutation({
    mutationFn: async () => {
      const valor = texto.trim() || null;
      if (campo === 'proveedor') {
        if (entry) return api.updateLedgerEntry(entry.id, { vendorName: valor });
        if (movement) return api.updateMovementDetails(movement.id, { vendorName: valor });
      }
      if (campo === 'observaciones') {
        if (entry) return api.updateLedgerEntry(entry.id, { notes: valor });
        if (movement) return api.updateMovementDetails(movement.id, { notes: valor });
        if (fila.payment) return api.updatePaymentNotes(fila.payment.id, valor);
      }
      if (campo === 'inmueble') {
        // Un cargo REPARTIDO entre pisos pasa a ser de uno solo por el cargo, no por su primera parte.
        if (fila.reparto && movement) return api.updateMovementDetails(movement.id, { propertyId: inmueble });
        if (entry) return api.updateLedgerEntry(entry.id, { propertyId: aPropertyId(inmueble) });
        if (movement) return api.updateMovementDetails(movement.id, { propertyId: inmueble });
      }
      if (campo === 'pagador' && movement && pagador) {
        return api.updateMovementDetails(movement.id, { tenantId: pagador, applyToSame: true });
      }
      return undefined;
    },
    onSuccess: (result) =>
      listo(
        campo === 'pagador'
          ? `Quién paga guardado en este movimiento${yIguales(result?.alsoChanged ?? 0)}.`
          : `${TITULO[campo]} guardado.`,
      ),
    onError: fallo,
  });

  // --- El papel: poner, cambiar o quitar ----------------------------------------
  const subir = useMutation({
    mutationFn: (file: File) => subirDocumentoDeFila(fila, file, inmueble),
    onSuccess: (result) => listo('Documento guardado.', result.reopened),
    onError: (error) => {
      if (error instanceof Error && error.message === 'inmueble') {
        toast.error('Elige primero de qué inmueble es el gasto.');
        return;
      }
      fallo(error);
    },
  });

  const quitar = useMutation({
    mutationFn: async () => {
      if (entry) return api.updateLedgerEntry(entry.id, { documentId: null });
      if (movement) return api.removeMovementDocument(movement.id);
      if (fila.payment) return api.removePaymentDocument(fila.payment.id);
      return undefined;
    },
    onSuccess: () => listo('Documento quitado. Sigue guardado en Documentos.'),
    onError: fallo,
  });

  const ocupado = guardar.isPending || subir.isPending || quitar.isPending;
  const documento = fila.soporte && fila.soporte !== 'extracto' ? fila.soporte : null;

  function enviar(event: FormEvent) {
    event.preventDefault();
    if (campo === 'inmueble' && !inmueble) return;
    if (campo === 'pagador' && !pagador) return;
    guardar.mutate();
  }

  const contenido =
    campo === 'soporte' ? (
      <div className={styles.cuerpo}>
        {documento ? (
          <p className={styles.actual}>
            <Icon name="file-lines" size={14} strokeWidth={2} />
            <span>{documento.name}</span>
          </p>
        ) : (
          <p className={styles.nota}>
            {fila.soporte === 'extracto'
              ? 'Lo justifica el extracto del banco. Puedes añadir además un justificante.'
              : 'Esta fila no tiene ningún documento todavía.'}
          </p>
        )}

        {cargoPendiente && (
          <label className={styles.campo}>
            <span className={styles.etiqueta}>Inmueble del gasto</span>
            <Select
              options={properties.map((p) => ({ value: p.id, label: p.name }))}
              placeholder="Que lo saque de la factura"
              value={inmueble}
              onChange={(event) => setInmueble(event.target.value)}
            />
            <span className={styles.nota}>
              Al subir el ticket, el cargo queda apuntado como gasto de ese inmueble. Sin elegirlo, lo saco del nombre del
              fichero o de la factura (su CUPS o su dirección).
            </span>
          </label>
        )}

        <input
          ref={ficheroRef}
          type="file"
          accept="application/pdf,image/*"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) subir.mutate(file);
          }}
        />
        <div className={styles.botones}>
          {documento && (
            <Button variant="danger" size="sm" onClick={() => quitar.mutate()} disabled={ocupado}>
              {quitar.isPending ? 'Quitando…' : 'Quitar'}
            </Button>
          )}
          <span className={styles.hueco} />
          <Button variant="secondary" size="sm" onClick={onClose} disabled={ocupado}>
            Cancelar
          </Button>
          <Button size="sm" onClick={() => ficheroRef.current?.click()} disabled={ocupado}>
            <Icon name="upload" size={14} strokeWidth={2.2} />
            {subir.isPending ? 'Subiendo…' : documento ? 'Cambiar documento' : 'Subir documento'}
          </Button>
        </div>
      </div>
    ) : (
      <form className={styles.cuerpo} onSubmit={enviar}>
        {campo === 'inmueble' ? (
          <>
            <Select
              options={(entry || movement?.kind === 'gasto'
                ? [...properties, { id: GASTOS_GENERALES_ID, name: GASTOS_GENERALES_NOMBRE }]
                : properties
              ).map(
                (p) => ({ value: p.id, label: p.name }),
              )}
              placeholder="Elige el inmueble…"
              value={inmueble}
              onChange={(event) => setInmueble(event.target.value)}
              aria-label="Inmueble"
            />
            {cargoPendiente && (
              <span className={styles.nota}>El cargo quedará apuntado como gasto de ese inmueble.</span>
            )}
          </>
        ) : campo === 'pagador' ? (
          <>
            <Select
              options={tenants.map((t) => ({ value: t.id, label: `${t.fullName} · ${t.label}` }))}
              placeholder="Elige quién paga…"
              value={pagador}
              onChange={(event) => setPagador(event.target.value)}
              aria-label="Quién paga"
            />
            {(() => {
              const elegido = tenants.find((t) => t.id === pagador);
              return elegido?.propertyName ? (
                <span className={styles.nota}>
                  Va a {elegido.propertyName}
                  {elegido.unitCode ? ` · ${elegido.unitCode}` : ''}.
                </span>
              ) : null;
            })()}
            {fila.pagador?.sugerido && pagador === fila.pagador.id && (
              <span className={styles.nota}>Propuesto: {fila.pagador.motivo ?? 'el más probable'}. Aún no está guardado.</span>
            )}
            <span className={styles.nota}>
              Al guardar se pone también en los ingresos exactamente iguales (mismo concepto y mismo importe) y se
              recuerda para los próximos extractos.
            </span>
          </>
        ) : campo === 'observaciones' ? (
          <Textarea
            value={texto}
            onChange={(event) => setTexto(event.target.value)}
            rows={3}
            maxLength={fila.payment && !entry && !movement ? 200 : 500}
            placeholder={fila.observaciones ?? 'Escribe una nota…'}
            aria-label="Observaciones"
            onKeyDown={(event) => {
              // Ctrl/⌘ + Enter guarda sin tener que ir al botón.
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                guardar.mutate();
              }
            }}
          />
        ) : (
          <Input
            value={texto}
            onChange={(event) => setTexto(event.target.value)}
            maxLength={120}
            placeholder="Iberdrola, Comunidad…"
            aria-label="Proveedor"
          />
        )}
        <div className={styles.botones}>
          <span className={styles.hueco} />
          <Button variant="secondary" size="sm" onClick={onClose} disabled={ocupado}>
            Cancelar
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={ocupado || (campo === 'inmueble' && !inmueble) || (campo === 'pagador' && !pagador)}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      </form>
    );

  return createPortal(
    <div
      ref={cajaRef}
      className={styles.caja}
      role="dialog"
      aria-label={`Editar ${TITULO[campo].toLowerCase()}`}
      style={{ width: ANCHO, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className={styles.cabecera}>
        <span className={styles.titulo}>{TITULO[campo]}</span>
        <span className={styles.subtitulo}>{fila.concepto}</span>
      </div>
      {contenido}
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// El menú «⋯» de la fila: borrar, quitar el papel, deshacer
// ---------------------------------------------------------------------------

type AccionFila = 'quitarDoc' | 'borrarApunte' | 'borrarCobro' | 'deshacer';

/**
 * Lo que se puede hacer con la fila desde su menú. Va en un menú y no en más iconos porque la
 * tabla ya lleva cuatro lápices y un ojo por fila: borrar se usa poco y no debe estar a mano
 * de un clic despistado.
 */
export function accionesDeFila(fila: FilaConciliacion): AccionFila[] {
  if (fila.prevista) return [];
  const acciones: AccionFila[] = [];
  const documento = fila.soporte && fila.soporte !== 'extracto';
  if (documento && puedeEditar(fila, 'soporte')) acciones.push('quitarDoc');
  if (fila.entry) acciones.push('borrarApunte');
  else if (fila.payment) acciones.push('borrarCobro');
  else if (fila.movement && fila.movement.status !== 'pendiente') acciones.push('deshacer');
  return acciones;
}

const ACCION: Record<AccionFila, { texto: string; icono: 'trash' | 'file' | 'transfer'; peligro: boolean }> = {
  quitarDoc: { texto: 'Quitar documento', icono: 'file', peligro: false },
  borrarApunte: { texto: 'Borrar apunte', icono: 'trash', peligro: true },
  borrarCobro: { texto: 'Borrar cobro', icono: 'trash', peligro: true },
  deshacer: { texto: 'Volver a pendiente', icono: 'transfer', peligro: false },
};

/** El botón «⋯» de la fila. Solo se ve al pasar por ella (o con el foco). */
export function BotonMenuFila({ activo, onOpen }: { activo: boolean; onOpen: (anchor: HTMLElement) => void }) {
  return (
    <button
      type="button"
      className={activo ? `${styles.menuBoton} ${styles.menuBotonActivo}` : styles.menuBoton}
      title="Más acciones"
      aria-label="Más acciones de la fila"
      aria-haspopup="menu"
      aria-expanded={activo}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(event.currentTarget);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="5" cy="12" r="1.8" fill="currentColor" />
        <circle cx="12" cy="12" r="1.8" fill="currentColor" />
        <circle cx="19" cy="12" r="1.8" fill="currentColor" />
      </svg>
    </button>
  );
}

const ANCHO_MENU = 220;

export function MenuFila({
  fila,
  anchor,
  onClose,
}: {
  fila: FilaConciliacion;
  anchor: HTMLElement;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const cajaRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  // Lo que se va a hacer y espera confirmación; mientras, el menú se esconde.
  const [confirmando, setConfirmando] = useState<AccionFila | null>(null);
  const acciones = accionesDeFila(fila);

  useLayoutEffect(() => {
    const caja = cajaRef.current;
    if (!caja || confirmando) return;
    const r = anchor.getBoundingClientRect();
    const alto = caja.offsetHeight;
    const left = Math.min(Math.max(12, r.right - ANCHO_MENU), window.innerWidth - ANCHO_MENU - 12);
    const debajo = r.bottom + 4;
    const top = debajo + alto > window.innerHeight - 12 ? Math.max(12, r.top - alto - 4) : debajo;
    setPos({ top, left });
  }, [anchor, confirmando]);

  useEffect(() => {
    if (confirmando) return undefined;
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    }
    function onDown(event: MouseEvent) {
      const target = event.target as Node;
      if (cajaRef.current?.contains(target) || anchor.contains(target)) return;
      onClose();
    }
    function onScroll(event: Event) {
      if (cajaRef.current?.contains(event.target as Node)) return;
      onClose();
    }
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    const timer = setTimeout(() => cajaRef.current?.querySelector<HTMLElement>('button')?.focus(), 30);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
      clearTimeout(timer);
    };
  }, [anchor, onClose, confirmando]);

  const hacer = useMutation({
    mutationFn: async (accion: AccionFila) => {
      const entry = fila.entry?.entry;
      const movement = fila.movement;
      switch (accion) {
        case 'quitarDoc':
          if (entry) return api.updateLedgerEntry(entry.id, { documentId: null });
          if (movement) return api.removeMovementDocument(movement.id);
          if (fila.payment) return api.removePaymentDocument(fila.payment.id);
          return undefined;
        case 'borrarApunte':
          return entry ? api.deleteLedgerEntry(entry.id) : undefined;
        case 'borrarCobro':
          return fila.payment ? api.deleteOffBankPayment(fila.payment.id) : undefined;
        case 'deshacer':
          return movement ? api.resolveMovement(movement.id, { action: 'reset' }) : undefined;
      }
    },
    onSuccess: async (_, accion) => {
      await queryClient.invalidateQueries({ queryKey: ['ledger'] });
      toast.success(
        accion === 'borrarApunte'
          ? 'Apunte borrado. Su factura sigue en Documentos.'
          : accion === 'borrarCobro'
            ? 'Cobro borrado. El justificante sigue en Documentos.'
            : accion === 'quitarDoc'
              ? 'Documento quitado. Sigue guardado en Documentos.'
              : 'El movimiento vuelve a estar pendiente.',
      );
      onClose();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido hacer.');
      setConfirmando(null);
    },
  });

  const documento = fila.soporte && fila.soporte !== 'extracto' ? fila.soporte : null;
  const enlazadoAlBanco = Boolean(fila.entry && fila.movement);
  const CONFIRMAR: Record<AccionFila, { titulo: string; cuerpo: string; boton: string }> = {
    quitarDoc: {
      titulo: 'Quitar el documento',
      cuerpo: `«${documento?.name ?? ''}» deja de justificar esta fila. El fichero sigue guardado en Documentos.`,
      boton: 'Quitar',
    },
    borrarApunte: {
      titulo: 'Borrar el apunte',
      cuerpo: `Se borra «${fila.concepto}» de la conciliación.${
        enlazadoAlBanco ? ' El cargo del banco que lo pagaba vuelve a quedar pendiente.' : ''
      } Si tenía factura, sigue guardada en Documentos.`,
      boton: 'Borrar',
    },
    borrarCobro: {
      titulo: 'Borrar el cobro',
      cuerpo: `Se borra «${fila.concepto}» y esa renta vuelve a quedar sin cobrar. El justificante, si lo tenía, sigue en Documentos.`,
      boton: 'Borrar',
    },
    deshacer: {
      titulo: 'Volver a pendiente',
      cuerpo: `Se deshace lo que se decidió de «${fila.concepto}» (a quién o a qué inmueble se asignó) y vuelve a quedar pendiente de resolver.`,
      boton: 'Volver a pendiente',
    },
  };

  return (
    <>
      {!confirmando &&
        createPortal(
          <div
            ref={cajaRef}
            className={styles.menu}
            role="menu"
            aria-label="Acciones de la fila"
            style={{ width: ANCHO_MENU, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            onClick={(event) => event.stopPropagation()}
          >
            {acciones.map((accion) => (
              <button
                key={accion}
                type="button"
                role="menuitem"
                className={ACCION[accion].peligro ? `${styles.menuItem} ${styles.menuItemPeligro}` : styles.menuItem}
                onClick={() => setConfirmando(accion)}
              >
                <Icon name={ACCION[accion].icono} size={15} strokeWidth={2} />
                {ACCION[accion].texto}
              </button>
            ))}
          </div>,
          document.body,
        )}
      {confirmando && (
        <ConfirmDialog
          title={CONFIRMAR[confirmando].titulo}
          body={CONFIRMAR[confirmando].cuerpo}
          confirmLabel={CONFIRMAR[confirmando].boton}
          tone={ACCION[confirmando].peligro || confirmando === 'quitarDoc' ? 'danger' : 'default'}
          onCancel={onClose}
          onConfirm={async () => {
            await hacer.mutateAsync(confirmando).catch(() => undefined);
          }}
        />
      )}
    </>
  );
}
